import React, { useState } from 'react';
import './ForgotPassword.css';
import { requestPasswordReset, resetPassword } from '../services/authApi';

const ForgotPassword = ({ onBack, onResetSuccess }) => {
  const [step, setStep] = useState('email'); // 'email' or 'reset'
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 1: Request password reset
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess('✅ Password reset email sent! Check your inbox for the reset link.');
      setStep('reset');
      // Clear email after successful submission
      setTimeout(() => {
        setEmail('');
      }, 500);
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
      console.error('Password reset error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Reset password with token
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords
    if (newPassword !== confirmPassword) {
      setError('❌ Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('❌ Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, newPassword);
      setSuccess('✅ Password changed successfully! You can now login with your new password.');
      
      setTimeout(() => {
        // Reset form and go back to login
        setStep('email');
        setEmail('');
        setToken('');
        setNewPassword('');
        setConfirmPassword('');
        onResetSuccess();
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to reset password. The link may have expired.');
      console.error('Password change error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-box">
        <div className="forgot-password-header">
          <h2>🔐 Password Recovery</h2>
          <button
            type="button"
            className="close-btn"
            onClick={onBack}
            disabled={loading}
            title="Back to Login"
          >
            ✕
          </button>
        </div>

        {step === 'email' ? (
          // Step 1: Email entry
          <form onSubmit={handleEmailSubmit} className="forgot-password-form">
            <p className="form-description">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                📧 Email Address
              </label>
              <input
                type="email"
                id="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your registered email"
                required
                disabled={loading}
              />
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !email}
            >
              {loading ? '🔄 Sending...' : '📬 Send Reset Link'}
            </button>
          </form>
        ) : (
          // Step 2: Password reset
          <form onSubmit={handlePasswordReset} className="forgot-password-form">
            <p className="form-description">
              Enter the reset token from the email and set your new password.
            </p>

            <div className="form-group">
              <label htmlFor="token" className="form-label">
                🔑 Reset Token
              </label>
              <input
                type="text"
                id="token"
                className="form-input"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste the token from the reset email"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                🔒 New Password
              </label>
              <input
                type="password"
                id="newPassword"
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                🔒 Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
                disabled={loading}
              />
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <button
              type="submit"
              className="submit-btn"
              disabled={loading || !token || !newPassword || !confirmPassword}
            >
              {loading ? '🔄 Changing...' : '✅ Change Password'}
            </button>

            <button
              type="button"
              className="back-btn"
              onClick={() => {
                setStep('email');
                setError('');
                setSuccess('');
                setToken('');
                setNewPassword('');
                setConfirmPassword('');
              }}
              disabled={loading}
            >
              ← Back to Email Entry
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
